import { auth } from "../../firebase";
import { useEffect, useRef, useState } from "react";
import companiesQuery from "../../firestore/Companies.query";
import { LinkMenus } from "../../model/LinkMenus";
import TableList, { GridCol, GridRow } from "../../components/table/TableList";
import Button from "../../components/button/Button";
import { Spiner } from "../../components/spinner/Spiner";
import { LinkMenusDeleteModal } from "./LinkMenusDeleteModal";
import { LinkMenusModal } from "./LinkMenusModal";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import Badge from "../../components/badges/Badges";
import { ConstMessage, CreateUserName } from "../../config/const";
import { Staff } from "../../model/Staff";

// 連携メニュー管理
export const LinkMenusPage = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isLoad, setIsLoad] = useState(true);
  const [linkMenusList, setLinkMenusList] = useState<LinkMenus[]>([]);
  const [linkMenus, setLinkMenus] = useState<LinkMenus | undefined>();
  const [deleteLinkMenus, setDeleteLinkMenus] = useState<LinkMenus | undefined>();
  const [staffList, setStaffList] = useState<Staff[]>([]);

  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;

  const dispatch = useDispatch();

  const isDisabled = useRef(false);

  useEffect(() => {
    const f = async () => {
      try {
        // 連携メニュー一覧取得
        const linkMenusList = await companiesQuery.getLinkMenusList(currentUser?.tenantId!);
        setLinkMenusList(linkMenusList);

        // スタッフ一覧取得
        const staffs = await companiesQuery.getStaffList(currentUser?.tenantId!);
        setStaffList(staffs);

      } catch (err: any) {
        console.error(err);
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));

      } finally {
        setIsLoad(false);
      }
    };
    f();
  }, [isOpen, dialogIsOpen, currentUser?.tenantId]);

  const columns: GridCol[] = [
    {
      field: "number",
      headerName: "No.",
      style: "w-1/12",
    },
    {
      field: "name",
      headerName: "名称",
      style: "",
    },
    {
      field: "type",
      headerName: "種別",
      style: "",
    },
    {
      field: "createType",
      headerName: "作成者",
      style: "",
    },
    {
      field: "control",
      headerName: "編集/削除",
      style: "w-2/12",
    },
  ];

  /**
   * 作成者を返却する
   * 
   * @param isAdmin 
   * @param createUserMail 
   * @returns 運営で作成：運営 / 団体で作成：作成者名（削除済の管理者の場合：団体）
   */
  const getRegisterName = (isAdmin: boolean, createUserMail?: string) => {
    if (isAdmin) {
      return CreateUserName.ADMIN;
    }
    // メールがない場合は団体
    if (!createUserMail) {
      return CreateUserName.MUNICIPALITY;
    }
    // メールでスタッフを検索
    const matchedStaff = staffList.find((staff: Staff) => staff.email === createUserMail);
    if (matchedStaff?.displayName) {
      return matchedStaff.displayName;
    }
    // 削除済or不明
    return CreateUserName.MUNICIPALITY;
  };

  const rowList: GridRow[] = linkMenusList.map((linkMenus, index) => {
    const isAdmin = linkMenus.createType === undefined || linkMenus.createType === 'admin';
    return {
      rowData: {
        number: index + 1,
        name: (<div className={"sm:whitespace-normal"}>{linkMenus.name ?? ''}</div>),
        type: linkMenus.type === "App"
          ? (<Badge color="pink" children="アプリ" />)
          : (<Badge color="indigo" children="Web" />),
        createType: getRegisterName(isAdmin, linkMenus.createUser),
        control: !isAdmin ? (
          <div className="flex space-x-2">
            <Button
              onClick={() => {
                setIsUpdate(true);
                setLinkMenus(linkMenus);
                toggle();
              }}
              className={"whitespace-nowrap"}
              children={"編集"}/>
            <Button
              color="red"
              onClick={() => {
                deleteDialogToggle();
                setDeleteLinkMenus(linkMenus);
              }}
              className={"whitespace-nowrap"}
              children={"削除"}/>
          </div>
        ) : ''
      },
      sortableRowData: {
        name: linkMenus.name ?? '',
        type: linkMenus.type ?? 'App',
        control: isAdmin
      }
    };
  });

  const onSubmit = async (data: LinkMenus) => {
    if(!isDisabled.current){
      try {
        const value: LinkMenus = {
          id: data?.id ?? '',
          type: data?.type ?? "Web",
          name: data.name,
          packageName: '',
          bundleID: '',
          url: data.url,
          createType: 'municipality',
          createUser: data.createUser ?? currentUser?.email!,
          createAt: data?.createAt ?? new Date(),
        };
        let linkMenuList = await companiesQuery.getLinkMenusList(currentUser?.tenantId!);
        // 編集対象の連携アプリ以外の連携アプリを取得
        linkMenuList = linkMenuList.filter(linkMenu => linkMenu.id !== data.id)
        // 連携アプリ名称が一致する連携アプリを取得し重複チェックを行う
        linkMenuList = linkMenuList.filter(linkMenu => linkMenu.name === data.name)
        if (linkMenuList.length > 0){
          // 重複した場合、dupCheckをtrueにする
          sessionStorage.setItem("dupCheck", "true");
        }
        // 重複しない場合、連携アプリ保存処理実行
        if(sessionStorage.getItem("dupCheck") === "false"){
          isDisabled.current = true;
        if(isUpdate) {
          await companiesQuery.updateLinkMenus(currentUser?.tenantId!, value);
        } else {
          await companiesQuery.setLinkMenus(currentUser?.tenantId!, value);
        };
        dispatch(alertOpen(AlertValue("連携アプリを保存しました", "success")));
        toggle();
        isDisabled.current = false;
        }
      } catch (err: any) {
        let errorMessage = err.code;
        if (errorMessage === "ERR_NETWORK") {
          dispatch(alertOpen(AlertValue("ネットワークに接続できません。", "danger")));
        } else {
          dispatch(alertOpen(AlertValue("保存に失敗しました", "danger")));
        }
        console.error(err);
        isDisabled.current = false;
      }
    }
  };

  const toggle = () => {
    setIsOpen(!isOpen);
  };
  const deleteDialogToggle = () => {
    setDialogIsOpen(!dialogIsOpen);
  };

  const onDelete = (linkMenus: LinkMenus) => {
    if(!isDisabled.current){
      try {
        isDisabled.current = true;
        deleteDialogToggle();
        companiesQuery.deleteLinkMenus(currentUser?.tenantId!, linkMenus);
        companiesQuery.deleteMenuSettingByMenuNo(currentUser?.tenantId!, linkMenus.id);
        dispatch(alertOpen(AlertValue("削除しました。", "success")));
        isDisabled.current = false;
      } catch(err: any) {
        console.error(err);
        isDisabled.current = false;
      }
    }
  }

  return (
    <div className="h-full">
      <LinkMenusModal
        children={undefined}
        open={isOpen}
        toggle={toggle}
        onSubmit={onSubmit}
        closeOnClickOutside={false}
        updateFlg={isUpdate}
        linkMenus={linkMenus}
        authorEmail={currentUser?.email!}
      />
      <LinkMenusDeleteModal
        children={undefined}
        open={dialogIsOpen}
        toggle={deleteDialogToggle}
        closeOnClickOutside={false}
        linkMenus={deleteLinkMenus}
        handle={onDelete}
      />
      <div className="sm:flex px-6 pt-6 pb-4 sm:items-center">
        <div className="sm:flex-auto">
          <h2 className="text-xl  font-semibold text-gray-900">
            連携ボタンメニュー設定
          </h2>
        </div>
        <div>
          <Button
            size={"sm"}
            onClick={() => {
              setIsUpdate(false);
              setLinkMenus(undefined);
              toggle();
            }}
            children={"連携ボタンメニュー新規作成"}
          />
        </div>
      </div>
      {isLoad === true ? <Spiner /> : (
        <div className={"px-6"}>
          <TableList
            header
            gridCol={columns}
            gridRow={rowList}
            defaultSortOrder={"desc"}
            limit={50}
          />
        </div>
      )}
    </div>
  );
}